/**
 * useCustomerPortal — mutation hook to open the Stripe Customer Portal.
 *
 * `openPortal()` POSTs to /api/billing/portal-session via `billingAgent`, then
 * redirects the browser to the returned `portal_url` with
 * `window.location.assign` (so the back button returns the user to /billing
 * rather than the page they left). The Customer Portal is where the user updates
 * their payment method, sees Stripe-hosted invoices, and actually cancels /
 * reactivates a subscription (design Screens 4/5/7 — cancellation is delegated
 * to the Portal, not handled in-app).
 *
 * UX safeguards (design §10 / plan §3):
 *   - `loading` flips true immediately so the CTA can show a spinner + disable.
 *   - `error` surfaces a human message; status codes map to friendly copy:
 *       · 400 "No billing account found" → "You don't have a billing account yet
 *         — choose a plan first." (the user never checked out).
 *       · 503 → "Billing isn't available yet."
 *       · 502 / other → the backend message (or a generic retry prompt).
 *   - An 8-second timeout fallback sets `error` and clears `loading` if no
 *     redirect URL comes back, so the user is never stranded on a dead click.
 *
 * Mock mode (for Playwright / local E2E without real Stripe) mirrors
 * `useStartCheckout`:
 *   - `process.env.REACT_APP_USE_MOCK_BILLING === 'true'`, OR
 *   - the page URL has `?stripeMockMode=1`
 *   short-circuits the network call and redirects straight to a mock portal
 *   landing (`/billing?portalReturn=1&mock=1`) so tests can drive the
 *   "Continue to cancel" / "Manage payment" path without contacting Stripe.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { billingAgent } from '../agents/billingAgent';
import { isMockBillingMode } from './useStartCheckout';

const PORTAL_TIMEOUT_MS = 8000;
/** Where the mock redirect lands — back on /billing, flagged as a portal return. */
export const MOCK_PORTAL_RETURN_URL = '/billing?portalReturn=1&mock=1';

/** Friendly copy for the contract's named error states. */
export const NO_BILLING_ACCOUNT_MESSAGE =
  "You don't have a billing account yet — choose a plan first.";
export const BILLING_UNAVAILABLE_MESSAGE = "Billing isn't available yet.";
const GENERIC_PORTAL_ERROR = "Couldn't open billing — please retry.";

export function useCustomerPortal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const openPortal = useCallback(async ({ returnUrl } = {}) => {
    setError(null);
    setLoading(true);

    // Mock mode: skip the network entirely and simulate the Stripe return so
    // Playwright can drive the cancel / manage-payment paths without Stripe.
    if (isMockBillingMode()) {
      window.location.assign(MOCK_PORTAL_RETURN_URL);
      return;
    }

    // Arm the 8s fallback so a hung request re-enables the CTA with a message.
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setError(GENERIC_PORTAL_ERROR);
      setLoading(false);
    }, PORTAL_TIMEOUT_MS);

    try {
      const data = await billingAgent.openCustomerPortal({ returnUrl });
      if (timerRef.current) clearTimeout(timerRef.current);

      const portalUrl = data?.portal_url;
      if (!portalUrl) {
        if (mountedRef.current) {
          setError(GENERIC_PORTAL_ERROR);
          setLoading(false);
        }
        return;
      }
      // Only navigate away once we have a confirmed URL (never strand the user).
      window.location.assign(portalUrl);
    } catch (err) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!mountedRef.current) return;
      const status = err?.status ?? err?.statusCode;
      if (status === 400) {
        setError(NO_BILLING_ACCOUNT_MESSAGE);
      } else if (status === 503) {
        setError(BILLING_UNAVAILABLE_MESSAGE);
      } else {
        setError(err?.message || GENERIC_PORTAL_ERROR);
      }
      setLoading(false);
    }
  }, []);

  return { openPortal, loading, error };
}

export default useCustomerPortal;
